import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Package, Clock, CircleDot, CheckCircle2 } from "lucide-react";

const JOB_STYLE = {
    open: "bg-gold-500/10 text-gold-700 border-gold-200",
    in_progress: "bg-navy-50 text-navy-700 border-navy-100",
    complete: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const JOB_LABEL = {
    open: "Open",
    in_progress: "In progress",
    complete: "Complete",
};
const JOB_ICON = { open: Clock, in_progress: CircleDot, complete: CheckCircle2 };

function money(amount, currency) {
    if (amount == null || !currency) return "—";
    return `${currency.toUpperCase()} ${(amount / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

function fmt(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function VendorPurchases() {
    const [data, setData] = useState({ open: 0, items: [] });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    const load = () =>
        api
            .get("/broker/purchases")
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));

    useEffect(() => {
        load();
        const t = setInterval(load, 20000);
        return () => clearInterval(t);
    }, []);

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/broker/purchases/${id}`, { job_status: status });
            toast.success(`Marked ${JOB_LABEL[status].toLowerCase()}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const rows =
        filter === "all"
            ? data.items
            : data.items.filter((r) => r.job_status === filter);

    const gross = data.items.reduce((s, r) => s + (r.amount || 0), 0);
    const reimburse = data.items.reduce(
        (s, r) => s + (r.vendor_share_amount || 0),
        0
    );

    return (
        <>
            <PageHeader
                eyebrow="Fulfilment"
                title="Purchases"
                description="Every Perx member who's bought your products. Each row is a job — mark it in-progress or complete as you fulfil it."
                testid="vendor-purchases-header"
                action={
                    data.open > 0 && (
                        <Badge
                            data-testid="vendor-purchases-open-badge"
                            className="bg-gold-500 text-navy-900 border-gold-200 border text-xs px-3 py-1 uppercase tracking-widest"
                        >
                            {data.open} open
                        </Badge>
                    )
                }
            />

            <div className="px-6 lg:px-12 py-8 space-y-6">
                <div className="grid sm:grid-cols-3 gap-5">
                    <StatTile icon={Package} label="Total purchases" value={data.items.length} />
                    <StatTile
                        icon={Package}
                        label="Gross billed"
                        value={money(gross, "hkd")}
                    />
                    <StatTile
                        icon={Package}
                        label="Your reimbursement"
                        value={money(reimburse, "hkd")}
                        accent
                    />
                </div>

                <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft overflow-hidden">
                    <div className="flex items-center justify-between px-6 py-4 border-b border-navy-100/70">
                        <div className="text-sm text-navy-500">
                            Live job feed. New purchases arrive here automatically and
                            an email is sent to your vendor address.
                        </div>
                        <Select value={filter} onValueChange={setFilter}>
                            <SelectTrigger
                                data-testid="vendor-purchases-filter"
                                className="w-44 bg-white border-navy-100 h-10"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                                <SelectItem value="all">All jobs</SelectItem>
                                <SelectItem value="open">Open</SelectItem>
                                <SelectItem value="in_progress">In progress</SelectItem>
                                <SelectItem value="complete">Complete</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <Table data-testid="vendor-purchases-table">
                        <TableHeader>
                            <TableRow className="bg-cream hover:bg-cream">
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Product</TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Buyer</TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Purchased</TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Your share</TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Job status</TableHead>
                                <TableHead className="text-right text-navy-700 uppercase text-xs tracking-[0.2em]">Action</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center text-navy-500 py-10">
                                        Loading…
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center text-navy-500 py-10">
                                        No purchases in this view yet.
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map((r) => {
                                const Icon = JOB_ICON[r.job_status] || Clock;
                                return (
                                    <TableRow
                                        key={r.id}
                                        data-testid={`vendor-purchase-row-${r.id}`}
                                        className={r.job_status === "open" ? "bg-gold-500/5" : ""}
                                    >
                                        <TableCell className="font-medium text-navy-700">
                                            {r.product_name}
                                            <div className="text-[11px] text-navy-500 font-normal font-mono mt-0.5">
                                                {r.item_id}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-navy-500">
                                            {r.buyer_email || "—"}
                                        </TableCell>
                                        <TableCell className="text-navy-500 text-sm">
                                            {fmt(r.created_at)}
                                        </TableCell>
                                        <TableCell className="text-navy-700 font-semibold">
                                            {money(r.vendor_share_amount, r.currency)}
                                            <div className="text-[11px] text-navy-500 font-normal mt-0.5">
                                                of {money(r.amount, r.currency)} · {r.vendor_share_pct}%
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                className={`${JOB_STYLE[r.job_status]} border inline-flex items-center gap-1.5`}
                                            >
                                                <Icon size={12} />
                                                {JOB_LABEL[r.job_status]}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {r.job_status === "open" && (
                                                <Button
                                                    size="sm"
                                                    data-testid={`purchase-start-${r.id}`}
                                                    onClick={() => setStatus(r.id, "in_progress")}
                                                    className="rounded-full h-8 bg-navy-700 hover:bg-navy-800 text-white"
                                                >
                                                    Start job
                                                </Button>
                                            )}
                                            {r.job_status === "in_progress" && (
                                                <Button
                                                    size="sm"
                                                    data-testid={`purchase-complete-${r.id}`}
                                                    onClick={() => setStatus(r.id, "complete")}
                                                    className="rounded-full h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                                                >
                                                    Mark complete
                                                </Button>
                                            )}
                                            {r.job_status === "complete" && (
                                                <span className="text-xs text-navy-500">Done</span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </>
    );
}

function StatTile({ icon: Icon, label, value, accent }) {
    return (
        <div className="rounded-2xl bg-white shadow-soft p-6">
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-[0.3em] text-navy-500">{label}</div>
                <Icon
                    size={18}
                    className={accent ? "text-gold-600" : "text-navy-500"}
                    strokeWidth={1.7}
                />
            </div>
            <div className="font-display text-3xl text-navy-700">{value}</div>
        </div>
    );
}
